/**
 * A profile-level collection-behavior control, distinct from `AssumedShape`'s
 * pricing inputs: how often the profile actually collects at night, not what
 * a night window costs. Off by default; the three fields are absent from the
 * DOM (not hidden) until the switch is on, so nothing here can be read or
 * tabbed to while it does not apply.
 *
 * Switching it on seeds all three fields with a default the api accepts
 * (`_require_night_reduction_window` in `api/src/socialsrc_api/schemas/
 * tracking.py`: present, with the max interval at or above its own
 * `INTERVAL_FLOOR_SECONDS`), so the section never opens half-built. A field
 * an operator then clears while the switch stays on is a local, blocking
 * error (`nightReductionValid`, read by `ProfileEditor`'s own save gate) --
 * the api would 422 on the half-built window regardless, and the field error
 * says so before that round trip.
 */

import { useId, type ReactNode } from 'react';

import { defineCatalogue } from '../../../../i18n';
import { Field, FieldDescription, FieldError, FieldLabel } from '../../../../ui/field';
import { DurationInput } from '../../../../chrome/form';
import { Input } from '../../../../ui/input';
import { Switch } from '../../../../ui/switch';

const nightReductionCatalogue = defineCatalogue(
  {
    title: 'Reduzir coletas à noite',
    'maxInterval.label': 'Frequência máxima à noite',
    'maxInterval.hint': 'A coleta não passa desse ritmo enquanto durar a redução.',
    'start.label': 'Início da redução',
    'stop.label': 'Fim da redução',
    required: 'Obrigatório enquanto a redução à noite está ativada.',
  },
  {
    title: 'Reduce night-time collection',
    'maxInterval.label': 'Maximum night-time frequency',
    'maxInterval.hint': 'Collection never runs faster than this while the reduction lasts.',
    'start.label': 'Reduction start',
    'stop.label': 'Reduction stop',
    required: 'Required while night-time reduction is on.',
  },
);
const nt = nightReductionCatalogue.t;

/** The default window a reader gets by switching the section on: one that
 *  `_require_night_reduction_window` always accepts (`DEFAULT_MAX_INTERVAL_SECONDS`
 *  clears `INTERVAL_FLOOR_SECONDS`), so turning it on never leaves a 422
 *  waiting at save. */
const DEFAULT_MAX_INTERVAL_SECONDS = 3600;
const DEFAULT_START_LOCAL = '23:00:00';
const DEFAULT_STOP_LOCAL = '06:00:00';

export interface NightReductionValue {
  night_reduction_enabled: boolean;
  night_reduction_max_interval_seconds: number | null;
  night_reduction_start_local: string | null;
  night_reduction_stop_local: string | null;
}

export const NIGHT_REDUCTION_SEED: NightReductionValue = {
  night_reduction_enabled: false,
  night_reduction_max_interval_seconds: null,
  night_reduction_start_local: null,
  night_reduction_stop_local: null,
};

/** `api`'s floor for any collection interval (core `INTERVAL_FLOOR_SECONDS`). */
const NIGHT_INTERVAL_FLOOR_SECONDS = 300;

/** The one presence rule the api enforces (`_require_night_reduction_window`): off needs
 *  nothing, on names all three fields. Read by `ProfileEditor`'s own save gate so a half-built
 *  window blocks locally rather than round-tripping to a 422. */
export function nightReductionValid(value: NightReductionValue): boolean {
  return (
    !value.night_reduction_enabled ||
    (value.night_reduction_max_interval_seconds !== null &&
      value.night_reduction_start_local !== null &&
      value.night_reduction_stop_local !== null)
  );
}

// `<input type="time">` reports and accepts `HH:mm`; a wire value carrying
// seconds (`HH:mm:ss`) is trimmed to what the control can round-trip.
function toTimeInputValue(raw: string | null): string {
  return raw === null ? '' : raw.slice(0, 5);
}

export function NightReduction(props: {
  value: NightReductionValue;
  onChange: (next: NightReductionValue) => void;
}): ReactNode {
  const { value, onChange } = props;
  const maxIntervalId = useId();
  const startId = useId();
  const stopId = useId();

  function setEnabled(enabled: boolean): void {
    onChange(
      enabled
        ? {
            night_reduction_enabled: true,
            night_reduction_max_interval_seconds: DEFAULT_MAX_INTERVAL_SECONDS,
            night_reduction_start_local: DEFAULT_START_LOCAL,
            night_reduction_stop_local: DEFAULT_STOP_LOCAL,
          }
        : NIGHT_REDUCTION_SEED,
    );
  }

  const maxIntervalMissing =
    value.night_reduction_enabled && value.night_reduction_max_interval_seconds === null;
  const startMissing = value.night_reduction_enabled && value.night_reduction_start_local === null;
  const stopMissing = value.night_reduction_enabled && value.night_reduction_stop_local === null;

  return (
    <section data-testid="night-reduction" className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-title-sm">{nt('title')}</h2>
        <Switch
          checked={value.night_reduction_enabled}
          onCheckedChange={setEnabled}
          aria-label={nt('title')}
        />
      </div>

      {value.night_reduction_enabled && (
        <div className="flex flex-col gap-4">
          <Field data-invalid={maxIntervalMissing}>
            <FieldLabel htmlFor={maxIntervalId}>{nt('maxInterval.label')}</FieldLabel>
            <DurationInput
              id={maxIntervalId}
              seconds={value.night_reduction_max_interval_seconds}
              onChange={(seconds) =>
                onChange({ ...value, night_reduction_max_interval_seconds: seconds })
              }
              minSeconds={NIGHT_INTERVAL_FLOOR_SECONDS}
              units={['minutes', 'hours', 'days']}
            />
            {maxIntervalMissing ? (
              <FieldError>{nt('required')}</FieldError>
            ) : (
              <FieldDescription>{nt('maxInterval.hint')}</FieldDescription>
            )}
          </Field>

          <Field data-invalid={startMissing}>
            <FieldLabel htmlFor={startId}>{nt('start.label')}</FieldLabel>
            <Input
              id={startId}
              type="time"
              aria-invalid={startMissing}
              value={toTimeInputValue(value.night_reduction_start_local)}
              onChange={(event) =>
                onChange({
                  ...value,
                  night_reduction_start_local: event.target.value || null,
                })
              }
            />
            {startMissing && <FieldError>{nt('required')}</FieldError>}
          </Field>

          <Field data-invalid={stopMissing}>
            <FieldLabel htmlFor={stopId}>{nt('stop.label')}</FieldLabel>
            <Input
              id={stopId}
              type="time"
              aria-invalid={stopMissing}
              value={toTimeInputValue(value.night_reduction_stop_local)}
              onChange={(event) =>
                onChange({
                  ...value,
                  night_reduction_stop_local: event.target.value || null,
                })
              }
            />
            {stopMissing && <FieldError>{nt('required')}</FieldError>}
          </Field>
        </div>
      )}
    </section>
  );
}
